import { type RefObject, useEffect, useState } from 'react';
import type { ChatComposerDraft } from './chat-composer-types.ts';

/**
 * What is written in the composer. A page's draft replaces it: a page hands
 * over a whole question, not a fragment to be worked into a sentence someone
 * was writing.
 */
export function useComposerText(
  draft: ChatComposerDraft | undefined,
  field: RefObject<HTMLTextAreaElement | null>,
) {
  const [text, setText] = useState('');

  /* Taken during the render that first sees a new id, so the field never shows the old text. */
  const draftId = draft?.id;
  const draftText = draft?.text;
  const [takenDraftId, setTakenDraftId] = useState(draftId);
  if (draftId !== takenDraftId) {
    setTakenDraftId(draftId);
    if (draftId !== undefined && draftId !== 0 && draftText !== undefined) {
      setText(draftText);
    }
  }

  /* The caret goes after the words, or focusing would leave it in front of them. */
  useEffect(() => {
    if (draftId === undefined || draftId === 0 || draftText === undefined) return;
    const element = field.current;
    if (!element) return;
    element.focus();
    element.setSelectionRange(draftText.length, draftText.length);
  }, [field, draftId, draftText]);

  return [text, setText] as const;
}
